import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction
} from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { VALIDATION_MESSAGES } from 'constants/txt'
import { notification } from 'components/Notification'
import { ITimeline } from 'components/Timelines/types'
import { TDocumentTypeUnion } from 'components/FileUpload/types'
import { setLoading } from 'redux/store/common/slice'
import { getUser } from 'redux/store/user/getters'
import {
  ContractCreationWays,
  ContractStatusUnion,
  IContractDetails,
  ManualContractCreationRequestData,
  TContractDocument,
  TContractDocumentTempStore,
  TContractUploadTempFile,
  TDocumentSignatoryToReassign,
  TGetDocumentsHistoryParams,
  TSignatory,
  VendorFeeTypeUnion
} from 'features/ContractDetails/types'
import {
  CONTRACT_DETAILS_TABS,
  CONTRACT_STATUS,
  INITIAL_CONTRACT_DETAILS,
  SIGNATORY_STATUS
} from 'features/ContractDetails/constants'
import {
  ACTIONS,
  contractSubject,
  SUBJECTS
} from 'features/ContractDetails/Abilities'
import {
  acceptContractDocumentRequestAsync,
  addSignatoryAction,
  createContractRequestAsync,
  extendContractDetailsRequestAsync,
  getContractDetailsRequestAsync,
  getContractDocumentsRequestAsync,
  reassignSignatoryAction,
  setupContractDetailsTimeRequestAsync,
  updateContractDetailsRequestAsync,
  updateContractRebateRequestAsync,
  updateExistingContractRebateRequestAsync,
  uploadContractAttachmentsRequestAsync,
  uploadNewContractDocumentsRequestAsync,
  getVersionHistoryAsync,
  setContractNoteAsync,
  updateContractNoteAsync,
  getVersionHistoryHospitalAsync,
  deleteContractDocumentRequestAsync,
  uploadUpdatedContractDocumentsRequestAsync,
  uploadContractDocumentToTempStore
} from 'features/ContractDetails/api'
import { useContractDetailsAbility } from 'features/ContractDetails/Providers/ContractDetailsAbilityProvider'
import { IRenewFinishDatePayload } from 'features/ContractDetails/ExtendContract/types'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import { timelinesRequestDataConverter } from 'features/ContractDetails/ContractDetailsTimelines/utils'
import {
  HSAmendmentRequestData,
  IAddHSAmendmentPayload,
  THSAmendmentFile,
  TLoPDocument
} from 'features/ContractDetails/Facilities/types'
import {
  addHSAmendmentRequestAsync,
  addHSAmendmentToHospitalRequestAsync,
  uploadHSAmendmentDocumentRequestAsync
} from 'features/ContractDetails/Facilities/api'
import { RebateDataRequest } from 'features/ContractDetails/RebateConditions/RebateConditionsForm/types'
import { contractDetailsRoute } from 'features/ContractDetails/ContractDetailsRouter/routes'
import useTabs, { TabsReturnActions, TabsReturnState } from 'hooks/useTabs'
import { convertDateToRequestFormat } from 'utils/moment'
import { ROLES } from 'features/Permission'
import { CONTRACT_STATUSES } from '../../../constants'
import {
  getContractItemsPendingTableData,
  getContractItemsPendingVendorsTableData
} from '../../ContractItems/api'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { ITEM_LIST_STATUS } from '../../ContractItems/constants'
import {
  fetchPaymentFrequency,
  fetchPaymentTerms,
  fetchShippingMethods,
  fetchVendorFeeTypes
} from '../../../pages/CRUDUser/api'
import { formatOptionIdValue } from '../../../helper/optionsFormatters'
import { TOption } from '../../../components/Select/types'
import { TAddSignatoryForm } from '../ContractDetailsGeneral/ContractDetailsDocuments/AddSignatoryPopup/types'
import {
  formatPhoneNumberToRequest,
  getUserCommunity
} from '../../../helper/common'
import { AxiosResponse } from 'axios'

type ContextProps = {
  state: {
    details: IContractDetails
    contractDocuments: TContractDocument[] | null
    canEditContract: boolean
    canEditRebateConditions: boolean
    isVendor: boolean
    canDeclineContract: boolean
    isCreation: boolean
    expiredAt?: string
    isVCAOrVFO?: boolean
    isVCS?: boolean
    isAllResponsibleUsersAdded: boolean
    isCommunity?: boolean
    isCommons?: boolean
    canAllContractInfo: boolean
    isOnboarded?: boolean
    isFilesUpdate?: boolean
    isPendingBannerVisible?: boolean
    paymentFrequencyOptions: TOption[]
    paymentTermsOptions: TOption[]
    isAutoGeneratedContract: boolean
    vendorCanSeeAddTeamMemberButton: boolean
    canAddSignatory: boolean
    canViewSignatory: boolean
    canViewRedliningBanners: boolean
    canUploadInitialDocument: boolean
    canDeleteNewDocument: boolean
    canUploadNewDocument: boolean
    canUploadNewDocumentVersion: boolean
    canUploadAttachments: boolean
    canDownloadTemplates: boolean
    canDownloadDocuments: boolean
    canAcceptContract: boolean
    canSeeLoPSignatories: boolean
    canDownloadAttachments: boolean
    canAddFacilityLoPSignatories: boolean
    signatoriesToReassign: TDocumentSignatoryToReassign[]
    loPHistoryDocuments: TLoPDocument[]
    vendorFeeTypeOptions: TOption[]
    hasContractDocsArray: boolean
    hasContractAttachmentsArray: boolean
    hasContractTemplatesArray: boolean
    shippingMethodOptions: TOption[]
    canSeeHealthSystem: boolean
  } & TabsReturnState
  actions: {
    handleUploadAttachmentsAsync: (
      files: File[],
      documentType: TDocumentTypeUnion
    ) => Promise<void>
    handleDeleteAttachmentsAsync: (uuid: string) => Promise<void>
    handleUploadContractDocumentAsync: (
      files: File[],
      documentType: TDocumentTypeUnion,
      documentId?: string
    ) => Promise<void>
    handleUploadContractDocumentToTempStoreAsync: (
      file: File,
      documentType: TDocumentTypeUnion
    ) => Promise<TContractDocumentTempStore | undefined>
    handleAttachContractDocumentsIdsAsync: (
      files: TContractUploadTempFile[],
      documentId?: string
    ) => Promise<void>
    handleDeleteContractDocumentAsync: (documentId: string) => Promise<void>
    handleAcceptContractDocumentAsync: (documentId: string) => Promise<void>
    getContractDetailsAsync: () => Promise<IContractDetails | undefined>
    updateContractDetailsStatusAsync: (
      status: ContractStatusUnion
    ) => Promise<void>
    updateContractDetailsAsync: (
      details: Partial<IContractDetails>
    ) => Promise<void>
    createContractAsync: (
      details: Partial<ManualContractCreationRequestData>
    ) => Promise<string | undefined>
    setupContractDetailsTimeAsync: (
      timelines: ITimeline<typeof CONTRACT_STATUS>[]
    ) => Promise<void>
    extendContractDetailsAsync: (
      details: IRenewFinishDatePayload
    ) => Promise<void>
    updateContractRebatesAsync: (
      details: RebateDataRequest,
      callback?: Function
    ) => Promise<string | undefined>
    uploadHSAmendmentDocumentAsync: (
      file: File
    ) => Promise<THSAmendmentFile | undefined>
    addAmendmentDocumentAsync: (
      hsId: string,
      hospitalId: string | null,
      hsAmendmentPayload: IAddHSAmendmentPayload,
      editMode: boolean
    ) => Promise<void>
    getPendingItemStateAsync: () => Promise<void>
    setIsFilesUpdate: (isUpdate: boolean) => void
    addSignatory: (values: TAddSignatoryForm, documentId: string) => void
    canAddSignatoryToDocument: (documentId: string) => boolean
    canReassignSignatory: (signatory: TSignatory) => boolean
    reassignSignatory: (values: TAddSignatoryForm, documentId: string) => void
    getVersionHistoryForHS: ({
      hsId,
      hospitalId
    }: TGetDocumentsHistoryParams) => void
    updateContractNote: (text: string) => void
    getCreationDataOptions: (vendor_fee_type: VendorFeeTypeUnion) => void
    setLedBy: Dispatch<SetStateAction<string>>
  } & TabsReturnActions
}

const ContractDetailsContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const createRoute = 'create'

const prepareRouteId = (routeId?: string) =>
  routeId ? (routeId === createRoute ? null : routeId) : null

const ContractDetailsContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const dispatch = useDispatch()
  const { id: routeId, tab } = useParams()
  const user = useSelector(getUser)
  const ability = useContractDetailsAbility()

  const {
    extendContractPopup,
    renewFinishDatePopup,
    contractStepsTimePopup,
    acceptContractDocumentPopup,
    addSignatoryPopup,
    addNotePopup
  } = useContractDetailsPopup()

  const [id, setId] = useState<string | null>(prepareRouteId(routeId))
  const [details, _setDetails] = useState<IContractDetails>(
    INITIAL_CONTRACT_DETAILS
  )
  const [contractDocuments, _setContractDocuments] = useState<
    TContractDocument[] | null
  >(null)

  const [isFilesUpdate, setIsFilesUpdate] = useState(false)
  const [isPendingBannerVisible, setIsPendingBannerVisible] = useState(false)
  const [paymentFrequencyOptions, setPaymentFrequencyOptions] = useState<
    TOption[]
  >([])
  const [paymentTermsOptions, setPaymentTermsOptions] = useState<TOption[]>([])
  const [loPHistoryDocuments, setLoPHistoryDocuments] = useState<
    TLoPDocument[]
  >([])
  const [vendorFeeTypeOptions, setVendorFeeTypeOptions] = useState<TOption[]>(
    []
  )
  const [shippingMethodOptions, setShippingMethodOptions] = useState<TOption[]>(
    []
  )
  const [ledBy, setLedBy] = useState(details.led_by)

  const { state: tabsState, actions: tabsActions } = useTabs({
    tabs: CONTRACT_DETAILS_TABS,
    activeTab: tab || CONTRACT_DETAILS_TABS[0].key
  })

  const isCreation = useMemo(
    () =>
      routeId === createRoute &&
      (user.role === ROLES.VICE_PRESIDENT ||
        user.role === ROLES.COMMONS_CONTRACT_STEWARD ||
        user.role === ROLES.COMMONS_VICE_PRESIDENT ||
        user.role === ROLES.CONTRACT_STEWARD),
    [routeId, user.role]
  )

  const isOnboarded = useMemo(
    () =>
      user.role !== ROLES.COMMUNITY_PRESIDENT
        ? user.role.includes('community') &&
          user.health_system !== details.creator?.health_system
        : false,
    [user.role, user.health_system, details.creator?.health_system]
  )

  const canEditContract = useMemo(
    () =>
      ability.can(ACTIONS.EDIT, contractSubject(SUBJECTS.CONTRACT, details)),
    [ability, details]
  )
  const isAutoGeneratedContract = useMemo(
    () => details.creation_way === ContractCreationWays.AUTO,
    [details]
  )

  const canReassignSignatory = useCallback(
    (signatory: TSignatory) => {
      return (
        isAutoGeneratedContract &&
        signatory.status !== SIGNATORY_STATUS.SIGNED &&
        (details.status === CONTRACT_STATUS.OUT_FOR_SIGNATURE ||
          details.status === CONTRACT_STATUS.REDLINING) &&
        (!!user.vendor
          ? signatory.created_by.includes('vendor')
          : !signatory.created_by.includes('vendor'))
      )
    },
    [isAutoGeneratedContract, details.status, user.vendor]
  )

  const canAddSignatory = useMemo(
    () =>
      isAutoGeneratedContract &&
      ability.can(ACTIONS.ADD, contractSubject(SUBJECTS.SIGNATORY, details)),
    [isAutoGeneratedContract, ability, details]
  )

  const canAddSignatoryToDocument = useCallback(
    (documentId: string) => {
      if (!canAddSignatory) return false

      const document = contractDocuments?.find((doc) => doc.uuid === documentId)
      if (!document) return false

      return !document.signatories?.some((signatory) =>
        user.vendor
          ? signatory.created_by.includes('vendor')
          : !signatory.created_by.includes('vendor')
      )
    },
    [canAddSignatory, contractDocuments, user.vendor]
  )
  const canViewSignatory = useMemo(
    () =>
      isAutoGeneratedContract &&
      ability.can(ACTIONS.VIEW, contractSubject(SUBJECTS.SIGNATORY, details)),
    [ability, details, isAutoGeneratedContract]
  )
  const canUploadInitialDocument = useMemo(
    () =>
      ability.can(
        ACTIONS.UPLOAD,
        contractSubject(SUBJECTS.INITIAL_DOCUMENT, details)
      ),
    [ability, details]
  )
  const canUploadNewDocument = useMemo(
    () =>
      ability.can(
        ACTIONS.UPLOAD,
        contractSubject(SUBJECTS.NEW_DOCUMENT, details)
      ),
    [ability, details]
  )
  const canDeleteNewDocument = useMemo(
    () =>
      ability.can(
        ACTIONS.DELETE,
        contractSubject(SUBJECTS.NEW_DOCUMENT, details)
      ),
    [ability, details]
  )
  const canUploadNewDocumentVersion = useMemo(
    () =>
      ability.can(
        ACTIONS.UPLOAD,
        contractSubject(SUBJECTS.NEW_DOCUMENT_VERSION, details)
      ),
    [ability, details]
  )
  const canUploadAttachments = useMemo(
    () =>
      ability.can(
        ACTIONS.UPLOAD,
        contractSubject(SUBJECTS.ATTACHMENTS, details)
      ),
    [ability, details]
  )
  const canDownloadTemplates = useMemo(
    () =>
      ability.can(
        ACTIONS.DOWNLOAD,
        contractSubject(SUBJECTS.TEMPLATES, details)
      ),
    [ability, details]
  )
  const canDownloadDocuments = useMemo(
    () =>
      ability.can(
        ACTIONS.DOWNLOAD,
        contractSubject(SUBJECTS.DOCUMENTS, details)
      ),
    [ability, details]
  )
  const canViewRedliningBanners = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        contractSubject(SUBJECTS.REDLINING_BANNERS, details)
      ),
    [ability, details]
  )
  const canAcceptContract = useMemo(
    () =>
      ability.can(ACTIONS.ACCEPT, contractSubject(SUBJECTS.CONTRACT, details)),
    [ability, details]
  )

  const canAddFacilityLoPSignatories = useMemo(
    () =>
      !isAutoGeneratedContract &&
      ability.can(
        ACTIONS.SUBMIT,
        contractSubject(SUBJECTS.LOP_SIGNATORY, details)
      ),
    [isAutoGeneratedContract, ability, details]
  )

  const canSeeLoPSignatories = useMemo(() => {
    return ability.can(
      ACTIONS.VIEW,
      contractSubject(SUBJECTS.LOP_SIGNATORY, details)
    )
  }, [details, ability])

  const canViewRebatesTab = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        contractSubject(SUBJECTS.REBATE_CONDITIONS, details)
      ),
    [ability, details]
  )
  const canViewItemListTab = useMemo(
    () =>
      ability.can(ACTIONS.VIEW, contractSubject(SUBJECTS.ITEM_LIST, details)),
    [ability, details]
  )
  const canAllContractInfo = useMemo(
    () =>
      ability.can(ACTIONS.VIEW, contractSubject(SUBJECTS.ALL_INFO, details)),
    [ability, details]
  )

  const canEditRebateConditions = useMemo(
    () =>
      ability.can(
        ACTIONS.EDIT,
        contractSubject(SUBJECTS.REBATE_CONDITIONS, details)
      ),
    [ability, details]
  )

  const canDownloadAttachments = useMemo(
    () =>
      ability.can(
        ACTIONS.DOWNLOAD,
        contractSubject(SUBJECTS.ATTACHMENTS, details)
      ),
    [ability, details]
  )

  const canSeeHealthSystem = useMemo(
    () =>
      ability.can(ACTIONS.VIEW, contractSubject(SUBJECTS.HEALTH_SYSTEM, user)),
    [ability, user]
  )

  const isVendor = useMemo(() => Boolean(user.vendor), [user.vendor])

  const vendorCanSeeAddTeamMemberButton = useMemo(
    () =>
      ability.can(
        ACTIONS.ADD,
        contractSubject(SUBJECTS.VENDOR_CONTRACT_STEWARD, details)
      ),
    [ability, details]
  )

  const canDeclineContract = useMemo(
    () =>
      isVendor &&
      user.role !== ROLES.VENDOR_ANALYST &&
      user.role !== ROLES.VENDOR_LEGAL,
    [isVendor, user.role]
  )
  const isCommunity = useMemo(() => Boolean(getUserCommunity(user)), [user])
  const isCommons = useMemo(() => Boolean(user.commons), [user.commons])
  const isVCAOrVFO = useMemo(
    () =>
      Boolean(
        user.role === ROLES.VENDOR_CONTRACT_ADMIN ||
          user.role === ROLES.VENDOR_FINANCE_OFFICER
      ),
    [user.role]
  )
  const isVCS = useMemo(
    () => Boolean(user.role === ROLES.VENDOR_CONTRACT_STEWARD),
    [user.role]
  )

  const expiredAt = useMemo(() => {
    if (
      ability.cannot(ACTIONS.VIEW, contractSubject(SUBJECTS.TIMELINES, details))
    ) {
      return
    }

    const contractTimeline = details.timelines?.find(
      (timeline) => timeline.status === details.status
    )

    return contractTimeline?.expire_at || moment().toISOString()
  }, [ability, details])

  const signatoriesToReassign = useMemo<TDocumentSignatoryToReassign[]>(
    () =>
      (contractDocuments ?? []).map((document) => ({
        document: document.uuid,
        signatory: document.signatories?.find((signatory) =>
          user.vendor
            ? signatory.created_by.includes('vendor')
            : !signatory.created_by.includes('vendor')
        )
      })),
    [user.vendor, contractDocuments]
  )

  const hasContractDocsArray = useMemo(
    () => Array.isArray(contractDocuments) && !!contractDocuments.length,
    [contractDocuments]
  )

  const hasContractAttachmentsArray = useMemo(
    () =>
      Array.isArray(details.contract_attachments) &&
      !!details.contract_attachments.length,
    [details.contract_attachments]
  )

  const hasContractTemplatesArray = useMemo(
    () =>
      Array.isArray(details.contract_document_templates) &&
      !!details.contract_document_templates.length,
    [details.contract_document_templates]
  )

  const getContractDetailsAsync = useCallback(async () => {
    if (!id) {
      throw new Error('Contract ID not provided')
    }

    try {
      dispatch(setLoading(true))

      const response = await getContractDetailsRequestAsync(id)

      if (response?.data) {
        const data: IContractDetails = {
          ...response.data,
          creator: {
            ...response.data.creator,
            health_system: response.data.creator?.health_system?.uuid || ''
          }
        }
        _setDetails(data)
        return data
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, id])

  const getContractDocumentsAsync = useCallback(async () => {
    if (!id) {
      throw new Error('Contract ID not provided')
    }

    try {
      const response = await getContractDocumentsRequestAsync(id)

      if (response?.data?.results) {
        _setContractDocuments(response.data.results)
      }
    } catch (e) {
      console.error(e)
    }
  }, [id])

  const getPendingItemStateAsync = useCallback(async () => {
    if (!details?.uuid) return
    if (
      !ability.can(ACTIONS.UPLOAD, contractSubject(SUBJECTS.ITEM_LIST, details))
    )
      return
    try {
      const request = isVendor
        ? getContractItemsPendingVendorsTableData
        : getContractItemsPendingTableData
      const params = { ...DEFAULT_PAGE }
      if (isVendor) {
        params['pending_substatus'] = [
          ITEM_LIST_STATUS.NEWLY_ADDED,
          ITEM_LIST_STATUS.REPLACED
        ]
      }

      const response = await request({ params: params }, details.uuid)

      setIsPendingBannerVisible(!!response.data.count)
    } catch (e) {
      console.error(e)
    }
  }, [details, isVendor, setIsPendingBannerVisible, ability])

  const handleDeleteAttachmentsAsync = useCallback(
    async (uuid: string) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        await updateContractDetailsRequestAsync(details.uuid, {
          contract_attachments_ids: details.contract_attachments
            .filter((i) => i.uuid !== uuid)
            ?.map((i) => i.uuid)
        })
        setIsFilesUpdate(true)
        await getContractDetailsAsync()
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      details.uuid,
      dispatch,
      getContractDetailsAsync,
      details.contract_attachments,
      setIsFilesUpdate
    ]
  )

  const handleUploadAttachmentsAsync = useCallback(
    async (files: File[], documentType: TDocumentTypeUnion) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        const formData = new FormData()
        formData.append(documentType, files[0], files[0].name)

        await uploadContractAttachmentsRequestAsync({
          contract: details.uuid,
          file: formData.get(documentType)
        })
        setIsFilesUpdate(true)
        await getContractDetailsAsync()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [details.uuid, dispatch, getContractDetailsAsync, setIsFilesUpdate]
  )

  const handleAttachContractDocumentsIdsAsync = useCallback(
    async (
      files: TContractUploadTempFile[],
      documentId?: string,
      withUpdate = true
    ) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }

      // update existing document if id was defined
      if (documentId) {
        await uploadUpdatedContractDocumentsRequestAsync(
          {
            contract: details.uuid,
            files
          },
          documentId
        )
      } else {
        await uploadNewContractDocumentsRequestAsync({
          contract: details.uuid,
          files
        })
      }
      if (withUpdate) {
        setIsFilesUpdate(true)
        await getContractDocumentsAsync()
        await getContractDetailsAsync()
      }
    },
    [details.uuid, getContractDocumentsAsync, getContractDetailsAsync]
  )

  const handleUploadContractDocumentToTempStoreAsync = useCallback(
    async (file: File, documentType: TDocumentTypeUnion) => {
      const formData = new FormData()
      formData.append(documentType, file, file.name)

      const result = await uploadContractDocumentToTempStore(
        formData.get(documentType)
      )

      return result?.data
    },
    []
  )

  const handleUploadContractDocumentAsync = useCallback(
    async (
      file: File[],
      documentType: TDocumentTypeUnion,
      documentId?: string
    ) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        const tempStoreResult =
          await handleUploadContractDocumentToTempStoreAsync(
            file[0],
            documentType
          )

        if (!tempStoreResult || !tempStoreResult?.uuid) {
          throw new Error('Failed to upload document to temp store')
        }

        await handleAttachContractDocumentsIdsAsync(
          [
            {
              uploaded_file: tempStoreResult?.uuid
            }
          ],
          documentId,
          false
        )
        setIsFilesUpdate(true)
        await getContractDocumentsAsync()
        await getContractDetailsAsync()
        notification.success({
          message: VALIDATION_MESSAGES.SM0044
        })
      } catch (e) {
        const { status, data } = e as AxiosResponse
        if (status === 400 && data?.non_field_errors?.length) {
          notification.error({
            message: data.non_field_errors[0]
          })
        }
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      details.uuid,
      dispatch,
      handleAttachContractDocumentsIdsAsync,
      handleUploadContractDocumentToTempStoreAsync,
      getContractDocumentsAsync,
      getContractDetailsAsync,
      setIsFilesUpdate
    ]
  )

  const handleDeleteContractDocumentAsync = useCallback(
    async (documentId: string) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        await deleteContractDocumentRequestAsync({
          contractId: details.uuid,
          documentId
        })
        setIsFilesUpdate(true)
        await getContractDocumentsAsync()
        await getContractDetailsAsync()
        notification.success({
          message: 'The file has been successfully deleted.'
        })
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [details.uuid, dispatch, getContractDocumentsAsync, getContractDetailsAsync]
  )

  const handleAcceptContractDocumentAsync = useCallback(
    async (documentId: string) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        await acceptContractDocumentRequestAsync({
          contractId: details.uuid,
          documentId
        })
        await getContractDocumentsAsync()
        await getContractDetailsAsync()
        acceptContractDocumentPopup.actions.close()
        notification.success({
          message: `Contract ${VALIDATION_MESSAGES.SM0009} accepted`
        })
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      acceptContractDocumentPopup.actions,
      details.uuid,
      dispatch,
      getContractDetailsAsync,
      getContractDocumentsAsync
    ]
  )

  const updateContractDetailsStatusAsync = useCallback(
    async (status: ContractStatusUnion) => {
      if (!id) {
        throw new Error('Contract ID not provided')
      }
      try {
        dispatch(setLoading(true))

        await updateContractDetailsRequestAsync(id, {
          status
        })
        if (status === CONTRACT_STATUS.OUT_FOR_SIGNATURE) {
          notification.success({
            message: VALIDATION_MESSAGES.SM00901
          })
        } else if (
          status === CONTRACT_STATUS.ACTIVE ||
          status === CONTRACT_STATUS.EXECUTED ||
          status === CONTRACT_STATUS.EXPIRED
        ) {
          notification.success({
            message: VALIDATION_MESSAGES.SM00902
          })
        } else {
          notification.success({
            message: `Contract ${VALIDATION_MESSAGES.SM0009} ${
              CONTRACT_STATUSES[status?.toUpperCase()]?.toLowerCase() || status
            }`
          })
        }

        await getContractDetailsAsync()
      } catch (e) {
        const err = e as AxiosResponse
        console.error(e)
        notification.error({ message: err.data[0] })
      } finally {
        dispatch(setLoading(false))
      }
    },
    [id, dispatch, getContractDetailsAsync]
  )

  const extendContractDetailsAsync = useCallback(
    async (data: IRenewFinishDatePayload) => {
      if (!id) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        const formData = new FormData()

        formData.append('contract', id)
        formData.append(
          'file',
          data.extensionDocument.file,
          data.extensionDocument.file.name
        )
        formData.append(
          'finish_date',
          convertDateToRequestFormat(data.newFinishDate)
        )

        await extendContractDetailsRequestAsync(formData)

        await getContractDetailsAsync()

        if (renewFinishDatePopup.state.visible) {
          renewFinishDatePopup.actions.close()
        }

        extendContractPopup.actions.close()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      id,
      dispatch,
      getContractDetailsAsync,
      renewFinishDatePopup.state.visible,
      renewFinishDatePopup.actions,
      extendContractPopup.actions
    ]
  )

  const createContractAsync = useCallback(
    async (details: Partial<ManualContractCreationRequestData>) => {
      try {
        dispatch(setLoading(true))

        const response = await createContractRequestAsync({
          ...details,
          creator: details.creator ?? user.uuid
        })

        return response?.data?.uuid
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, user.uuid]
  )

  const updateContractDetailsAsync = useCallback(
    async (details: Partial<IContractDetails>) => {
      if (!id) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        await updateContractDetailsRequestAsync(id, details)
        notification.success({
          message: VALIDATION_MESSAGES.CONTRACT_HAS_BEEN_SAVED
        })

        await getContractDetailsAsync()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [id, dispatch, getContractDetailsAsync]
  )

  const setupContractDetailsTimeAsync = useCallback(
    async (timelines: ITimeline<typeof CONTRACT_STATUS>[]) => {
      if (!id) {
        throw new Error('Contract ID not provided')
      }

      const requestData = timelinesRequestDataConverter(timelines)

      try {
        dispatch(setLoading(true))

        await setupContractDetailsTimeRequestAsync(id, requestData)

        await getContractDetailsAsync()

        contractStepsTimePopup.actions.close()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [id, dispatch, getContractDetailsAsync, contractStepsTimePopup.actions]
  )

  const updateContractNote = useCallback(
    async (text: string) => {
      try {
        dispatch(setLoading(true))

        if (details.note?.uuid) {
          await updateContractNoteAsync(details.uuid, details.note.uuid, {
            text
          })
        } else {
          await setContractNoteAsync(details.uuid, { text })
        }
        addNotePopup.actions.close()
        await getContractDetailsAsync()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      dispatch,
      details.note,
      details.uuid,
      addNotePopup.actions,
      getContractDetailsAsync
    ]
  )

  const updateContractRebatesAsync = useCallback(
    async (formData: RebateDataRequest, successCallback?: Function) => {
      if (!id) {
        throw new Error('Contract ID not provided')
      }

      const typeChanged = formData?.rebate_type !== details.rebate_type
      const rebateUuid = details.rebate?.uuid

      try {
        dispatch(setLoading(true))

        if (!typeChanged && rebateUuid) {
          await updateExistingContractRebateRequestAsync(
            id,
            rebateUuid,
            formData
          )
        } else {
          await updateContractRebateRequestAsync(id, formData)
        }

        const savedDetails = await getContractDetailsAsync()

        notification.success({ message: VALIDATION_MESSAGES.SM0047 })
        if (successCallback) {
          successCallback()
        }

        return (
          savedDetails?.rebate?.uuid ?? (!typeChanged ? rebateUuid : undefined)
        )
      } catch (e) {
        console.error(e)
        return undefined
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      id,
      dispatch,
      getContractDetailsAsync,
      details.rebate_type,
      details.rebate?.uuid
    ]
  )

  const uploadHSAmendmentDocumentAsync = useCallback(
    async (file: File) => {
      if (!id) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        const formData = new FormData()

        formData.append('file', file, file.name)

        const response = await uploadHSAmendmentDocumentRequestAsync(formData)

        return response.data
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [id, dispatch]
  )

  const _getRequiredCreationData = useCallback(() => {
    if (!canAllContractInfo && !canEditContract && !isCreation) return
    const led_by = details.uuid ? details.led_by : ledBy
    fetchVendorFeeTypes(led_by).then((vendorFeeTypes) => {
      if (vendorFeeTypes?.data?.results) {
        setVendorFeeTypeOptions(
          vendorFeeTypes.data.results.map(formatOptionIdValue)
        )
      }
    })
  }, [
    canAllContractInfo,
    canEditContract,
    details.led_by,
    details.uuid,
    isCreation,
    ledBy
  ])

  const getCreationDataOptions = useCallback(
    (vendor_fee_type: VendorFeeTypeUnion) => {
      if (!canAllContractInfo && !canEditContract && !isCreation) return
      Promise.all([
        fetchPaymentTerms(),
        fetchPaymentFrequency(vendor_fee_type)
      ]).then(([paymentTerms, paymentFrequency]) => {
        if (paymentTerms?.data?.results) {
          setPaymentTermsOptions(
            paymentTerms.data.results.map(formatOptionIdValue)
          )
        }
        if (paymentFrequency?.data?.results) {
          setPaymentFrequencyOptions(
            paymentFrequency.data.results.map(formatOptionIdValue)
          )
        }
      })
    },
    [canAllContractInfo, canEditContract, isCreation]
  )

  useEffect(() => {
    _getRequiredCreationData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.led_by, isCreation, ledBy])

  useEffect(() => {
    if (details.vendor_fee_type) {
      getCreationDataOptions(details.vendor_fee_type)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.vendor_fee_type])

  useEffect(() => {
    if (details.shipping_method || isCreation) {
      getShippingMethodOptions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.shipping_method, isCreation])

  const getShippingMethodOptions = useCallback(async () => {
    const { data } = await fetchShippingMethods()
    if (data?.results) {
      setShippingMethodOptions(data.results.map(formatOptionIdValue))
    }
  }, [])

  const getVersionHistoryForHS = useCallback(
    async ({ hsId, hospitalId }: TGetDocumentsHistoryParams) => {
      const {
        data: { results }
      } = hospitalId
        ? await getVersionHistoryHospitalAsync(details.uuid, hospitalId)
        : await getVersionHistoryAsync(details.uuid, hsId)
      if (results.length) {
        setLoPHistoryDocuments(results)
      }
    },
    [details]
  )

  const addAmendmentDocumentAsync = useCallback(
    async (
      hsId: string,
      hospitalId: string | null,
      hsAmendmentPayload: IAddHSAmendmentPayload,
      editMode: boolean
    ) => {
      if (!id) {
        throw new Error('Contract ID not provided')
      }
      if (!hsId) {
        throw new Error('Health System ID not provided')
      }
      try {
        dispatch(setLoading(true))
        let requestData: HSAmendmentRequestData = {
          file:
            typeof hsAmendmentPayload.file === 'string'
              ? hsAmendmentPayload.file
              : hsAmendmentPayload.file?.uuid || '',
          pricing_effective_date: hsAmendmentPayload.pricing_effective_date,
          initial_calculation_date: hsAmendmentPayload.initial_calculation_date,
          is_signature_required: hsAmendmentPayload.is_signature_required
        }
        if (
          hsAmendmentPayload.vendor_signatory &&
          hsAmendmentPayload.community_signatory
        ) {
          requestData = {
            ...requestData,
            vendor_signatory: hsAmendmentPayload.vendor_signatory,
            community_signatory: hsAmendmentPayload.community_signatory
          }
        }
        if (hospitalId)
          await addHSAmendmentToHospitalRequestAsync(
            id,
            hospitalId,
            requestData,
            editMode
          )
        else await addHSAmendmentRequestAsync(id, hsId, requestData, editMode)
        notification.success({
          message: 'Participation details have been successfully saved.'
        })
      } finally {
        dispatch(setLoading(false))
      }
    },
    [id, dispatch]
  )

  const addSignatory = useCallback(
    async (values: TAddSignatoryForm, documentId: string) => {
      if (!documentId) return

      try {
        dispatch(setLoading(true))

        const response = await addSignatoryAction(id, {
          ...values,
          phone_number: values.phone_number
            ? formatPhoneNumberToRequest(values.phone_number) ?? undefined
            : undefined,
          document: documentId
        })

        if (response?.data) {
          addSignatoryPopup.actions.close()
          await getContractDocumentsAsync()
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, id, addSignatoryPopup.actions, getContractDocumentsAsync]
  )

  const reassignSignatory = useCallback(
    async (values: TAddSignatoryForm, documentId: string) => {
      const signatoryToReassign = signatoriesToReassign.find(
        (item) => item.document === documentId
      )?.signatory

      if (!signatoryToReassign) return

      try {
        dispatch(setLoading(true))

        const signatory = {
          signatory: values.phone_number ? null : values.signatory,
          email: values.phone_number ? values.signatory : null,
          phone_number: values.phone_number
            ? formatPhoneNumberToRequest(values.phone_number)
            : null
        }

        const response = await reassignSignatoryAction(
          id,
          signatoryToReassign.uuid,
          signatory
        )

        if (response?.data) {
          addSignatoryPopup.actions.close()
          await getContractDocumentsAsync()
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      signatoriesToReassign,
      dispatch,
      id,
      addSignatoryPopup.actions,
      getContractDocumentsAsync
    ]
  )

  const _adjustTabsAccessibility = useCallback(() => {
    tabsActions.setTabs(
      CONTRACT_DETAILS_TABS.map((i) => {
        const inCreationDisabled =
          i.key !== contractDetailsRoute.general && isCreation

        const isItemListDisabled =
          !canViewItemListTab && i.key === contractDetailsRoute.itemList
        const isRebatesDisabled =
          !canViewRebatesTab && i.key === contractDetailsRoute.rebateConditions

        return {
          ...i,
          disabled: inCreationDisabled,
          danger: isItemListDisabled || isRebatesDisabled
        }
      })
    )
  }, [isCreation, tabsActions, canViewItemListTab, canViewRebatesTab])

  useEffect(() => {
    getPendingItemStateAsync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.uuid])

  const isAllResponsibleUsersAdded = useMemo(
    () =>
      isVendor
        ? Boolean(
            details.vendor_responsibles.find(
              (u) => u.role === ROLES.VENDOR_ANALYST
            ) &&
              details.vendor_responsibles.find(
                (u) => u.role === ROLES.VENDOR_LEGAL
              )
          )
        : isCommons
        ? Boolean(
            details.commons_responsibles.find(
              (u) => u.role === ROLES.COMMONS_ANALYST
            ) &&
              details.commons_responsibles.find(
                (u) => u.role === ROLES.COMMONS_LEGAL
              )
          )
        : Boolean(
            details.community_responsibles.find(
              (u) => u.role === ROLES.ANALYST
            ) &&
              details.community_responsibles.find((u) => u.role === ROLES.LEGAL)
          ),
    [
      isVendor,
      isCommons,
      details.community_responsibles,
      details.vendor_responsibles,
      details.commons_responsibles
    ]
  )

  useLayoutEffect(() => {
    _adjustTabsAccessibility()
  }, [_adjustTabsAccessibility])

  useEffect(() => {
    if (!isCreation) {
      getContractDetailsAsync()
      getContractDocumentsAsync()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContractDetailsAsync, isCreation])

  useEffect(() => {
    setId(prepareRouteId(routeId))
  }, [routeId])

  const context = useMemo(
    () => ({
      state: {
        details,
        contractDocuments,
        canEditContract,
        canEditRebateConditions,
        isVendor,
        isCreation,
        expiredAt,
        isVCAOrVFO,
        isVCS,
        ...tabsState,
        isAllResponsibleUsersAdded,
        isCommunity,
        isCommons,
        canDeclineContract,
        canAllContractInfo,
        isOnboarded,
        isFilesUpdate,
        isPendingBannerVisible,
        paymentTermsOptions,
        paymentFrequencyOptions,
        isAutoGeneratedContract,
        vendorCanSeeAddTeamMemberButton,
        canAddSignatory,
        canViewSignatory,
        canViewRedliningBanners,
        canUploadInitialDocument,
        canUploadNewDocumentVersion,
        canDeleteNewDocument,
        canUploadNewDocument,
        canUploadAttachments,
        canDownloadTemplates,
        canDownloadDocuments,
        canAcceptContract,
        canSeeLoPSignatories,
        canAddFacilityLoPSignatories,
        signatoriesToReassign,
        loPHistoryDocuments,
        vendorFeeTypeOptions,
        hasContractDocsArray,
        hasContractAttachmentsArray,
        hasContractTemplatesArray,
        canDownloadAttachments,
        shippingMethodOptions,
        canSeeHealthSystem
      },
      actions: {
        ...tabsActions,
        isVCAOrVFO,
        handleDeleteAttachmentsAsync,
        handleUploadAttachmentsAsync,
        handleUploadContractDocumentAsync,
        handleUploadContractDocumentToTempStoreAsync,
        handleAttachContractDocumentsIdsAsync,
        handleDeleteContractDocumentAsync,
        handleAcceptContractDocumentAsync,
        getContractDetailsAsync,
        updateContractDetailsStatusAsync,
        createContractAsync,
        updateContractDetailsAsync,
        setupContractDetailsTimeAsync,
        extendContractDetailsAsync,
        updateContractRebatesAsync,
        uploadHSAmendmentDocumentAsync,
        addAmendmentDocumentAsync,
        setIsFilesUpdate,
        getPendingItemStateAsync,
        addSignatory,
        canAddSignatoryToDocument,
        canReassignSignatory,
        reassignSignatory,
        getVersionHistoryForHS,
        updateContractNote,
        getCreationDataOptions,
        setLedBy
      }
    }),
    [
      details,
      contractDocuments,
      canEditContract,
      canEditRebateConditions,
      isVendor,
      isCreation,
      expiredAt,
      isVCAOrVFO,
      isVCS,
      tabsState,
      isAllResponsibleUsersAdded,
      isCommunity,
      isCommons,
      canDeclineContract,
      canAllContractInfo,
      isOnboarded,
      isFilesUpdate,
      isPendingBannerVisible,
      paymentTermsOptions,
      paymentFrequencyOptions,
      isAutoGeneratedContract,
      vendorCanSeeAddTeamMemberButton,
      canAddSignatory,
      canViewSignatory,
      canViewRedliningBanners,
      canUploadInitialDocument,
      canUploadNewDocumentVersion,
      canDeleteNewDocument,
      canUploadNewDocument,
      canUploadAttachments,
      canDownloadTemplates,
      canDownloadDocuments,
      canAcceptContract,
      canSeeLoPSignatories,
      canAddFacilityLoPSignatories,
      signatoriesToReassign,
      loPHistoryDocuments,
      vendorFeeTypeOptions,
      tabsActions,
      handleDeleteAttachmentsAsync,
      handleUploadAttachmentsAsync,
      handleUploadContractDocumentAsync,
      handleUploadContractDocumentToTempStoreAsync,
      handleAttachContractDocumentsIdsAsync,
      handleDeleteContractDocumentAsync,
      handleAcceptContractDocumentAsync,
      getContractDetailsAsync,
      updateContractDetailsStatusAsync,
      createContractAsync,
      updateContractDetailsAsync,
      setupContractDetailsTimeAsync,
      extendContractDetailsAsync,
      updateContractRebatesAsync,
      uploadHSAmendmentDocumentAsync,
      addAmendmentDocumentAsync,
      getPendingItemStateAsync,
      addSignatory,
      canAddSignatoryToDocument,
      canReassignSignatory,
      reassignSignatory,
      getVersionHistoryForHS,
      updateContractNote,
      getCreationDataOptions,
      hasContractDocsArray,
      hasContractAttachmentsArray,
      hasContractTemplatesArray,
      canDownloadAttachments,
      setLedBy,
      shippingMethodOptions,
      canSeeHealthSystem
    ]
  )

  return (
    <ContractDetailsContext.Provider value={context}>
      {children}
    </ContractDetailsContext.Provider>
  )
}

export const useContractDetailsContext = () =>
  useContext(ContractDetailsContext)

export default ContractDetailsContextProvider
